import React, { useEffect, useState } from 'react';
import { CountrySelector } from './CountrySelector';
import { CurrencySelector } from './CurrencySelector';
import { ContactList } from './ContactList';
import { AddressList } from './AddressList';
import { ContactForm } from './ContactForm';
import { AddressForm } from './AddressForm';
import { LogoManagement, LogoAsset } from './LogoManagement';
import { SaveIcon, XIcon, PlusIcon } from 'lucide-react';
export function SiteDetail({
  site,
  entity,
  onSave,
  onCancel,
  locale
}) {
  const [formData, setFormData] = useState({
    id: '',
    name: '',
    code: '',
    country: '',
    currency: '',
    activeFrom: '',
    activeTo: '',
    isActive: true,
    contacts: [],
    addresses: []
  });
  const [activeTab, setActiveTab] = useState('details');
  const [showContactForm, setShowContactForm] = useState(false);
  const [showAddressForm, setShowAddressForm] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [editingAddress, setEditingAddress] = useState(null);
  const [logos, setLogos] = useState<LogoAsset[]>([{
    id: 'logo-001',
    scope: 'Tenant',
    fileName: 'company-logo.png',
    url: 'https://uploadthingy.s3.us-west-1.amazonaws.com/wDZe3XuJbKbBWqS4Gjsurv/Rapid_P2P_-_Blue-Green.svg',
    contentType: 'image/png',
    uploadedAt: '2023-01-15T10:30:00Z',
    uploadedByUserId: 'user-001',
    uploadedByUserName: 'Admin User',
    isActive: true
  }, {
    id: 'logo-002',
    scope: 'Entity',
    entityId: entity?.id,
    entityName: entity?.name,
    fileName: 'entity-logo.png',
    url: 'https://uploadthingy.s3.us-west-1.amazonaws.com/wDZe3XuJbKbBWqS4Gjsurv/Rapid_P2P_-_Blue-Green.svg',
    contentType: 'image/png',
    uploadedAt: '2023-02-20T14:45:00Z',
    uploadedByUserId: 'user-001',
    uploadedByUserName: 'Admin User',
    isActive: true
  }]);
  useEffect(() => {
    if (site) {
      setFormData({
        ...site,
        contacts: site.contacts || [],
        addresses: site.addresses || []
      });
    } else {
      // Default values for new site
      setFormData({
        id: '',
        name: '',
        code: '',
        country: entity?.country || '',
        currency: entity?.currency || '',
        activeFrom: new Date().toISOString().split('T')[0],
        activeTo: '',
        isActive: true,
        contacts: [],
        addresses: []
      });
    }
  }, [site, entity]);
  const handleChange = e => {
    const {
      name,
      value,
      type,
      checked
    } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };
  const handleCountryChange = countryCode => {
    setFormData({
      ...formData,
      country: countryCode
    });
  };
  const handleCurrencyChange = currencyCode => {
    setFormData({
      ...formData,
      currency: currencyCode
    });
  };
  const handleSubmit = e => {
    e.preventDefault();
    onSave(formData);
  };
  const handleAddContact = () => {
    setEditingContact(null);
    setShowContactForm(true);
  };
  const handleEditContact = contact => {
    setEditingContact(contact);
    setShowContactForm(true);
  };
  const handleSaveContact = contactData => {
    if (contactData.id) {
      // Update existing contact
      setFormData({
        ...formData,
        contacts: formData.contacts.map(contact => contact.id === contactData.id ? contactData : contact)
      });
    } else {
      // Add new contact
      const newContact = {
        ...contactData,
        id: `con-${new Date().getTime()}`
      };
      setFormData({
        ...formData,
        contacts: [...formData.contacts, newContact]
      });
    }
    setShowContactForm(false);
  };
  const handleDeleteContact = contactId => {
    setFormData({
      ...formData,
      contacts: formData.contacts.filter(contact => contact.id !== contactId)
    });
  };
  const handleAddAddress = () => {
    setEditingAddress(null);
    setShowAddressForm(true);
  };
  const handleEditAddress = address => {
    setEditingAddress(address);
    setShowAddressForm(true);
  };
  const handleSaveAddress = addressData => {
    if (addressData.id) {
      // Update existing address
      setFormData({
        ...formData,
        addresses: formData.addresses.map(address => address.id === addressData.id ? addressData : address)
      });
    } else {
      // Add new address
      const newAddress = {
        ...addressData,
        id: `addr-${new Date().getTime()}`
      };
      setFormData({
        ...formData,
        addresses: [...formData.addresses, newAddress]
      });
    }
    setShowAddressForm(false);
  };
  const handleDeleteAddress = addressId => {
    setFormData({
      ...formData,
      addresses: formData.addresses.filter(address => address.id !== addressId)
    });
  };
  // Logo management handlers
  const handleLogoUpload = (file: File, scope: 'Tenant' | 'Entity' | 'Site', entityId?: string, siteId?: string) => {
    // Add document type information to the file
    const documentType = 'Logo';
    // In a real app, this would upload to a server
    // Here we'll create a local URL for demo purposes
    const reader = new FileReader();
    reader.onload = () => {
      const newLogo: LogoAsset = {
        id: `logo-${new Date().getTime()}`,
        scope,
        entityId,
        entityName: entity?.name,
        siteId: formData.id || undefined,
        siteName: formData.name || undefined,
        fileName: file.name,
        url: reader.result as string,
        contentType: file.type,
        uploadedAt: new Date().toISOString(),
        uploadedByUserId: 'user-001',
        uploadedByUserName: 'Admin User',
        isActive: true,
        documentType: documentType
      };
      // Deactivate other logos with the same scope and entity/site
      const updatedLogos = logos.map(logo => {
        if (logo.scope === scope && (scope === 'Tenant' || scope === 'Entity' && logo.entityId === entityId || scope === 'Site' && logo.siteId === siteId)) {
          return {
            ...logo,
            isActive: false
          };
        }
        return logo;
      });
      setLogos([...updatedLogos, newLogo]);
    };
    reader.readAsDataURL(file);
  };
  const handleLogoDelete = (logoId: string) => {
    setLogos(logos.filter(logo => logo.id !== logoId));
  };
  const handleSetActiveLogo = (logoId: string) => {
    const logoToActivate = logos.find(logo => logo.id === logoId);
    if (!logoToActivate) return;
    const updatedLogos = logos.map(logo => {
      if (logo.scope === logoToActivate.scope && (logo.scope === 'Tenant' || logo.scope === 'Entity' && logo.entityId === logoToActivate.entityId || logo.scope === 'Site' && logo.siteId === logoToActivate.siteId)) {
        return {
          ...logo,
          isActive: logo.id === logoId
        };
      }
      return logo;
    });
    setLogos(updatedLogos);
  };
  return <div className="bg-white shadow overflow-hidden sm:rounded-lg" data-id="element-17511">
      <div className="px-4 py-5 sm:px-6 flex justify-between items-center" data-id="element-17512">
        <div data-id="element-17513">
          <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-17514">
            {site ? 'Edit Site' : 'Create Site'}
          </h3>
          <p className="mt-1 max-w-2xl text-sm text-gray-500" data-id="element-17515">
            {site ? `Update site details for ${entity.name}` : `Enter details for the new site under ${entity.name}`}
          </p>
        </div>
        <div className="flex space-x-3" data-id="element-17516">
          <button type="button" onClick={onCancel} className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-17517">
            <XIcon className="h-4 w-4 mr-2" data-id="element-17518" />
            Cancel
          </button>
          <button type="button" onClick={handleSubmit} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-17519">
            <SaveIcon className="h-4 w-4 mr-2" data-id="element-17520" />
            Save Site
          </button>
        </div>
      </div>
      {/* Tabs */}
      <div className="border-b border-gray-200" data-id="element-17521">
        <nav className="-mb-px flex" aria-label="Tabs" data-id="element-17522">
          <button className={`${activeTab === 'details' ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} w-1/4 py-4 px-1 text-center border-b-2 font-medium text-sm`} onClick={() => setActiveTab('details')} data-id="element-17523">
            Details
          </button>
          <button className={`${activeTab === 'contacts' ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} w-1/4 py-4 px-1 text-center border-b-2 font-medium text-sm`} onClick={() => setActiveTab('contacts')} data-id="element-17524">
            Contacts
          </button>
          <button className={`${activeTab === 'addresses' ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} w-1/4 py-4 px-1 text-center border-b-2 font-medium text-sm`} onClick={() => setActiveTab('addresses')} data-id="element-17525">
            Addresses
          </button>
          <button className={`${activeTab === 'logo' ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} w-1/4 py-4 px-1 text-center border-b-2 font-medium text-sm`} onClick={() => setActiveTab('logo')} data-id="element-17526">
            Logo
          </button>
        </nav>
      </div>
      {/* Details Tab */}
      {activeTab === 'details' && <div className="p-4 sm:p-6" data-id="element-17527">
          <form className="space-y-6" data-id="element-17528">
            <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6" data-id="element-17529">
              <div className="sm:col-span-3" data-id="element-17530">
                <label htmlFor="name" className="block text-sm font-medium text-gray-700" data-id="element-17531">
                  Site Name
                </label>
                <div className="mt-1" data-id="element-17532">
                  <input type="text" name="name" id="name" value={formData.name} onChange={handleChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" required data-id="element-17533" />
                </div>
              </div>
              <div className="sm:col-span-3" data-id="element-17534">
                <label htmlFor="code" className="block text-sm font-medium text-gray-700" data-id="element-17535">
                  Site Code
                </label>
                <div className="mt-1" data-id="element-17536">
                  <input type="text" name="code" id="code" value={formData.code} onChange={handleChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" required data-id="element-17537" />
                </div>
              </div>
              <div className="sm:col-span-3" data-id="element-17538">
                <label htmlFor="country" className="block text-sm font-medium text-gray-700" data-id="element-17539">
                  Country
                </label>
                <div className="mt-1" data-id="element-17540">
                  <CountrySelector value={formData.country} onChange={handleCountryChange} locale={locale} data-id="element-17541" />
                </div>
              </div>
              <div className="sm:col-span-3" data-id="element-17542">
                <label htmlFor="currency" className="block text-sm font-medium text-gray-700" data-id="element-17543">
                  Default Currency
                </label>
                <div className="mt-1" data-id="element-17544">
                  <CurrencySelector value={formData.currency} onChange={handleCurrencyChange} countryCode={formData.country} locale={locale} data-id="element-17545" />
                </div>
              </div>
              <div className="sm:col-span-3" data-id="element-17546">
                <label htmlFor="activeFrom" className="block text-sm font-medium text-gray-700" data-id="element-17547">
                  Active From
                </label>
                <div className="mt-1" data-id="element-17548">
                  <input type="date" name="activeFrom" id="activeFrom" value={formData.activeFrom} onChange={handleChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-17549" />
                </div>
              </div>
              <div className="sm:col-span-3" data-id="element-17550">
                <label htmlFor="activeTo" className="block text-sm font-medium text-gray-700" data-id="element-17551">
                  Active To
                </label>
                <div className="mt-1" data-id="element-17552">
                  <input type="date" name="activeTo" id="activeTo" value={formData.activeTo} onChange={handleChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-17553" />
                </div>
              </div>
              <div className="sm:col-span-6" data-id="element-17554">
                <div className="flex items-center" data-id="element-17555">
                  <input id="isActive" name="isActive" type="checkbox" checked={formData.isActive} onChange={handleChange} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded" data-id="element-17556" />
                  <label htmlFor="isActive" className="ml-2 block text-sm text-gray-700" data-id="element-17557">
                    Is Active
                  </label>
                </div>
              </div>
            </div>
          </form>
        </div>}
      {/* Contacts Tab */}
      {activeTab === 'contacts' && <div className="p-4 sm:p-6" data-id="element-17558">
          {showContactForm ? <ContactForm contact={editingContact} onSave={handleSaveContact} onCancel={() => setShowContactForm(false)} locale={locale} data-id="element-17559" /> : <>
              <div className="mb-4 flex justify-end" data-id="element-17560">
                <button type="button" onClick={handleAddContact} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-17561">
                  <PlusIcon className="h-4 w-4 mr-2" data-id="element-17562" />
                  Add Contact
                </button>
              </div>
              <ContactList contacts={formData.contacts} onEdit={handleEditContact} onDelete={handleDeleteContact} locale={locale} data-id="element-17563" />
            </>}
        </div>}
      {/* Addresses Tab */}
      {activeTab === 'addresses' && <div className="p-4 sm:p-6" data-id="element-17564">
          {showAddressForm ? <AddressForm address={editingAddress} onSave={handleSaveAddress} onCancel={() => setShowAddressForm(false)} locale={locale} data-id="element-17565" /> : <>
              <div className="mb-4 flex justify-end" data-id="element-17566">
                <button type="button" onClick={handleAddAddress} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-17567">
                  <PlusIcon className="h-4 w-4 mr-2" data-id="element-17568" />
                  Add Address
                </button>
              </div>
              <AddressList addresses={formData.addresses} onEdit={handleEditAddress} onDelete={handleDeleteAddress} locale={locale} data-id="element-17569" />
            </>}
        </div>}
      {/* Logo Tab */}
      {activeTab === 'logo' && <div className="p-4 sm:p-6" data-id="element-17570">
          <LogoManagement scope="Site" entityId={entity?.id} entityName={entity?.name} siteId={formData.id} siteName={formData.name} logos={logos} onUpload={handleLogoUpload} onDelete={handleLogoDelete} onSetActive={handleSetActiveLogo} locale={locale} data-id="element-17571" />
        </div>}
    </div>;
}